import React, { useState, useEffect, useCallback } from "react";
import { db } from "@/api/db";
import { Users, Check, X, Eye, Loader2, Clock, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const STATUS_STYLE = {
  pending:  { bg: "#FEEDB4", text: "#65541B", label: "Pendente" },
  approved: { bg: "#B0FCEA", text: "#003326", label: "Aprovada" },
  rejected: { bg: "#FADBDF", text: "#892E38", label: "Rejeitada" },
};

function TabButton({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 text-sm font-semibold rounded-full transition-colors flex items-center gap-2"
      style={
        active
          ? { background: "#fff", color: "#00664C", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }
          : { color: "#646C6F" }
      }
    >
      {label}
      {count != null && (
        <span
          className="text-xs px-1.5 py-0.5 rounded-full font-bold"
          style={active ? { background: "#B0FCEA", color: "#003326" } : { background: "#E5E9EA", color: "#99A6AB" }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ActionBtn({ onClick, disabled, icon: Icon, label, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-opacity disabled:opacity-50"
      style={style}
    >
      {disabled ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export default function AdminPartnerApplications() {
  const [tab, setTab] = useState("pending");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [detailApp, setDetailApp] = useState(null);
  const [rejectApp, setRejectApp] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const all = await db.entities.PartnerApplication.list("-created_at");
      setApps(Array.isArray(all) ? all : []);
    } catch (err) {
      console.error("AdminPartnerApplications load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadApps(); }, [loadApps]);

  async function withLoading(id, fn) {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try { await fn(); } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }

  async function approve(app) {
    await withLoading(app.id, async () => {
      // Create MarketplaceCourse as draft
      const courseData = {
        title: app.product_name,
        description: `Por ${app.expert_name}. Público-alvo: ${app.target_audience}`,
        url: app.sales_page_url,
        original_price: app.product_price,
        price: app.has_discount && app.discount_value ? app.discount_value : app.product_price,
        level: app.level,
        professions: app.professions || [],
        instructor_name: app.expert_name,
        is_published: false,
      };

      const course = await db.entities.MarketplaceCourse.create(courseData);

      await db.entities.PartnerApplication.update(app.id, {
        status: "approved",
        reviewed_at: new Date().toISOString(),
        marketplace_course_id: course.id,
      });

      await loadApps();
    });
  }

  function openReject(app) {
    setRejectApp(app);
    setRejectReason("");
  }

  async function confirmReject() {
    if (!rejectApp) return;
    setRejecting(true);
    try {
      await db.entities.PartnerApplication.update(rejectApp.id, {
        status: "rejected",
        reviewed_at: new Date().toISOString(),
        rejection_reason: rejectReason || null,
      });
      setRejectApp(null);
      await loadApps();
    } finally {
      setRejecting(false);
    }
  }

  const pending  = apps.filter(a => a.status === "pending");
  const approved = apps.filter(a => a.status === "approved");
  const rejected = apps.filter(a => a.status === "rejected");
  const tabApps  = tab === "pending" ? pending : tab === "approved" ? approved : rejected;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#B0FCEA" }}>
          <Users className="w-5 h-5" style={{ color: "#00664C" }} />
        </div>
        <div>
          <h1 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Aplicações de Parceiros
          </h1>
          <p className="text-xs" style={{ color: "#99A6AB" }}>
            Revise e aprove aplicações de experts e escolas para o Guia de Formações
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl w-fit" style={{ background: "#E5E9EA" }}>
        <TabButton active={tab === "pending"}  onClick={() => setTab("pending")}  label="Pendentes"  count={pending.length} />
        <TabButton active={tab === "approved"} onClick={() => setTab("approved")} label="Aprovadas"  count={approved.length} />
        <TabButton active={tab === "rejected"} onClick={() => setTab("rejected")} label="Rejeitadas" count={rejected.length} />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-2xl p-4 bg-white animate-pulse" style={{ border: "1px solid #E5E9EA" }}>
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl flex-shrink-0" style={{ background: "#E5E9EA" }} />
                <div className="flex-1 space-y-2">
                  <div className="h-4 rounded-full w-1/2" style={{ background: "#E5E9EA" }} />
                  <div className="h-3 rounded-full w-1/3" style={{ background: "#E5E9EA" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : tabApps.length === 0 ? (
        <div className="rounded-2xl p-12 flex flex-col items-center gap-3 text-center bg-white" style={{ border: "1px solid #E5E9EA" }}>
          <Users className="w-9 h-9 opacity-20" style={{ color: "#00664C" }} />
          <p className="text-sm" style={{ color: "#99A6AB" }}>
            {tab === "pending"  && "Nenhuma aplicação pendente."}
            {tab === "approved" && "Nenhuma aplicação aprovada."}
            {tab === "rejected" && "Nenhuma aplicação rejeitada."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tabApps.map(app => {
            const busy = !!actionLoading[app.id];
            const ss = STATUS_STYLE[app.status] || STATUS_STYLE.pending;
            return (
              <div key={app.id} className="rounded-2xl p-4 bg-white flex gap-3" style={{ border: "1px solid #E5E9EA" }}>
                {/* Avatar initial */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ background: "#00664C" }}>
                  {app.expert_name?.[0]?.toUpperCase() || "?"}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm" style={{ color: "#2E3233", fontFamily: "Poppins, sans-serif" }}>
                      {app.product_name}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: ss.bg, color: ss.text }}>
                      {ss.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs" style={{ color: "#99A6AB" }}>
                    <span className="font-medium" style={{ color: "#646C6F" }}>{app.expert_name}</span>
                    <span>{app.level}</span>
                    <span>{app.product_price}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(app.created_at)}
                    </span>
                  </div>
                  {app.status === "rejected" && app.rejection_reason && (
                    <p className="text-xs mt-1" style={{ color: "#892E38" }}>
                      Motivo: {app.rejection_reason}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ActionBtn
                    onClick={() => setDetailApp(app)}
                    disabled={false}
                    icon={Eye}
                    label="Detalhes"
                    style={{ background: "#E5E9EA", color: "#646C6F" }}
                  />
                  {tab === "pending" && (
                    <>
                      <ActionBtn onClick={() => approve(app)} disabled={busy} icon={Check} label="Aprovar"
                        style={{ background: "#B0FCEA", color: "#003326" }} />
                      <ActionBtn onClick={() => openReject(app)} disabled={busy} icon={X} label="Rejeitar"
                        style={{ background: "#FADBDF", color: "#892E38" }} />
                    </>
                  )}
                  {tab === "rejected" && (
                    <ActionBtn onClick={() => approve(app)} disabled={busy} icon={Check} label="Aprovar"
                      style={{ background: "#B0FCEA", color: "#003326" }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailApp} onOpenChange={() => setDetailApp(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              Detalhes da Aplicação
            </DialogTitle>
          </DialogHeader>
          {detailApp && (
            <div className="space-y-4 pt-1">
              <Field label="Expert / Escola" value={detailApp.expert_name} />
              <Field label="Telefone" value={detailApp.phone} />
              <Field label="Nome do Produto" value={detailApp.product_name} />
              <Field label="Público-alvo" value={detailApp.target_audience} />
              <Field label="Nível" value={detailApp.level} />
              <Field label="Ticket do Produto" value={detailApp.product_price} />
              <Field label="Desconto?" value={detailApp.has_discount ? `Sim — ${detailApp.discount_value || ""}` : "Não"} />
              <Field label="Comissão" value={detailApp.commission_pct ? `${detailApp.commission_pct}%` : "—"} />
              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: "#003326" }}>Página de Vendas</p>
                <a href={detailApp.sales_page_url} target="_blank" rel="noopener noreferrer"
                  className="text-sm flex items-center gap-1.5 hover:underline" style={{ color: "#008572" }}>
                  {detailApp.sales_page_url} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <Field label="Plataforma" value={detailApp.platform} />
              <div>
                <p className="text-xs font-semibold mb-1.5" style={{ color: "#003326" }}>Profissões</p>
                <div className="flex flex-wrap gap-1.5">
                  {(detailApp.professions || []).map(p => (
                    <span key={p} className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: "#F2F4F4", color: "#646C6F" }}>
                      {p}
                    </span>
                  ))}
                  {(detailApp.professions || []).length === 0 && (
                    <span className="text-xs" style={{ color: "#99A6AB" }}>Nenhuma selecionada</span>
                  )}
                </div>
              </div>
              <Field label="Produto Perpétuo?" value={detailApp.is_perpetual ? "Sim" : "Não"} />
              <Field label="Data da Aplicação" value={formatDate(detailApp.created_at)} />
              {detailApp.reviewed_at && (
                <Field label="Data da Revisão" value={formatDate(detailApp.reviewed_at)} />
              )}
              {detailApp.rejection_reason && (
                <Field label="Motivo da Rejeição" value={detailApp.rejection_reason} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectApp} onOpenChange={() => setRejectApp(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              Rejeitar Aplicação
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <p className="text-sm" style={{ color: "#646C6F" }}>
              Tem certeza que deseja rejeitar a aplicação de <strong>{rejectApp?.expert_name}</strong> ({rejectApp?.product_name})?
            </p>
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                Motivo (opcional)
              </label>
              <Textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Ex: Produto não alinhado com o público da plataforma..."
                className="resize-none"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setRejectApp(null)}
                className="flex-1 py-3 rounded-full text-sm font-semibold"
                style={{ background: "#F2F4F4", color: "#646C6F" }}>
                Cancelar
              </button>
              <button onClick={confirmReject} disabled={rejecting}
                className="flex-1 py-3 rounded-full text-sm font-bold transition-opacity"
                style={{ background: "#FADBDF", color: "#892E38" }}>
                {rejecting ? "Rejeitando..." : "Confirmar Rejeição"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold mb-0.5" style={{ color: "#003326" }}>{label}</p>
      <p className="text-sm" style={{ color: "#646C6F" }}>{value || "—"}</p>
    </div>
  );
}
